import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Paper, BottomNavigation, BottomNavigationAction, Badge, Snackbar } from "@mui/material";
import { RestaurantMenu, RoomService, NotificationsActive } from "@mui/icons-material";
import { useOrder } from "../context/OrderContext";
import { useSession } from "../context/SessionContext";
import { apiClient } from "../utils/http";

// Not a route — BottomNavigation's onChange fires for every action the same
// way, so this fake "path" is how the center action is told apart from the
// two real tabs and handled as an API call instead of a navigate().
const CALL_WAITER_VALUE = "call-waiter";

// Just long enough to absorb an accidental double-tap on the same request —
// short enough, and visibly counted down below, that a guest whose waiter
// hasn't shown up yet can genuinely call again without it looking stuck.
// (An earlier 60s silent disable with no countdown was easy to mistake for
// broken, since nothing on screen showed it was actually still working.)
const CALL_WAITER_COOLDOWN_SECONDS = 15;

const TABS = [
  { path: "/", label: "Menu", icon: <RestaurantMenu /> },
  { path: "/live-order", label: "My Order", icon: <RoomService /> },
];

export default function BottomNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isLive } = useOrder();
  const { table } = useSession();
  const current = TABS.find((t) => t.path === pathname)?.path || false;

  const [calling, setCalling] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [snack, setSnack] = useState("");
  const cooldownInterval = useRef(null);

  useEffect(() => () => clearInterval(cooldownInterval.current), []);

  const startCooldown = () => {
    clearInterval(cooldownInterval.current);
    setSecondsLeft(CALL_WAITER_COOLDOWN_SECONDS);
    cooldownInterval.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(cooldownInterval.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const onCooldown = secondsLeft > 0;

  const callWaiter = async () => {
    if (!table?.id || calling || onCooldown) return;
    setCalling(true);
    try {
      // POST v1/call-waiter — OrderController@callWaiter — broadcasts
      // WaiterCalled on pos.orders.{storeId}, the same channel denzo-waiter
      // listens on for order events, with this table attached.
      await apiClient.post("call-waiter", { table: table.id });
      setSnack("Waiter has been notified.");
      startCooldown();
    } catch (err) {
      setSnack(err.message || "Couldn't call the waiter, please try again.");
    } finally {
      setCalling(false);
    }
  };

  const handleChange = (_, value) => {
    if (value === CALL_WAITER_VALUE) {
      callWaiter();
      return;
    }
    navigate(value);
  };

  return (
    <>
      <Paper elevation={8} sx={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 20 }}>
        <BottomNavigation value={current} onChange={handleChange} showLabels>
          <BottomNavigationAction label={TABS[0].label} value={TABS[0].path} icon={TABS[0].icon} sx={{ minWidth: 0 }} />
          <BottomNavigationAction
            label={onCooldown ? `Called · ${secondsLeft}s` : "Call Waiter"}
            value={CALL_WAITER_VALUE}
            disabled={!table?.id || calling || onCooldown}
            icon={<NotificationsActive />}
            sx={{ minWidth: 0 }}
          />
          <BottomNavigationAction
            label={TABS[1].label}
            value={TABS[1].path}
            icon={
              isLive ? (
                <Badge color="primary" variant="dot">
                  {TABS[1].icon}
                </Badge>
              ) : (
                TABS[1].icon
              )
            }
            sx={{ minWidth: 0 }}
          />
        </BottomNavigation>
      </Paper>

      <Snackbar
        open={!!snack}
        autoHideDuration={2500}
        onClose={() => setSnack("")}
        message={snack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ mb: 7 }}
      />
    </>
  );
}
